import { CreatedEntityIdentifier } from "@/api/CreatedEntityIdentifier";
import ModernApiController from "@/api/ModernApiController";
import { OwnUserGetDto } from "@/api/OwnUserGetDto";
import { UserFilterPostDto } from "@/api/UserFilterPostDto";
import { UserGetDto } from "@/api/UserGetDto";
import { IsProblemResult } from "@/helpers/newUserSelector";
import { AccountType, OrganizationUnit, PagedResult, User, UserGroup, UserType } from "@infoland/iprova-api";

export default {
    /**
     * Gets a list of users
     * @param filter (optional) A filter to use to find only specific users
     * @param offset (optional, default 0) The offset from which to start returning users from the list
     * @param maxResults (optional, default 100) The maximum users to return at one time
     * @param suppressErrors Whether to supress the throwing of exceptions on error
     * @returns a paged result of users
     */
    async getUsersAsync(
        filter: UserFilterPostDto = undefined,
        offset: number = 0,
        maxResults: number = 100,
        suppressErrors: boolean = false
    ): Promise<PagedResult<User> | null> {
        let path: string;

        if (filter && filterRequiresPost(filter)) {
            const filterResponse: CreatedEntityIdentifier = await ModernApiController.postAsync<UserFilterPostDto, CreatedEntityIdentifier>(
                "/users/filter",
                filter,
                suppressErrors
            );

            if (!IsProblemResult(filterResponse)) {
                const createdIdentifier: string = filterResponse.created_identifier;
                path = `/users/filter/${createdIdentifier}?offset=${offset}&limit=${maxResults}&envelope=false`;
            } else return null;
        } else {
            path = `/users?offset=${offset}&limit=${maxResults}&envelope=false`;

            if (filter != undefined) {
                // add simple filters to the path
                if (filter.name_contains) path += `&name=${encodeURIComponent(filter.name_contains)}`;
                if (filter.login_code) path += `&login_code=${encodeURIComponent(filter.login_code)}`;
                if (filter.name_login_code_email_contains)
                    path += `&name_login_code_email_contains=${encodeURIComponent(filter.name_login_code_email_contains)}`;
                if (filter.has_email !== undefined) path += `&has_email=${filter.has_email ? "true" : "false"}`;
                if (filter.user_group_id) path += `&user_group_id=${encodeURIComponent(filter.user_group_id)}`;
                if (filter.active) path += `&is_active=true`;
            }
        }
        const userResponse: PagedResult<UserGetDto> = await ModernApiController.getPagedResultAsync<UserGetDto>(path, suppressErrors);

        if (!IsProblemResult(userResponse)) {
            //convert v5 api dto's to v4 DTO's so we don't need to refactor the whole app
            return {
                ...userResponse,
                items: userResponse.items.map(u => ({
                    ...u,
                    date_format: undefined,
                    language: undefined,
                    show_favorites_and_recent_panel: undefined,
                    show_preferences: undefined,
                    show_tasks_and_subscriptions: undefined,
                    start_portal: undefined,
                    time_zone_id: undefined,
                    statistics: undefined,
                    two_step_authentication_allowed: undefined,
                    two_step_deadline_datetime: undefined,
                    two_step_otp_auth_url: undefined,
                    account_type: u.account_type == "group" ? AccountType.group : AccountType.personal,
                    active: u.active,
                    email_address: u.email_address,
                    external_id: undefined,
                    login_code: u.login_code,
                    manager: u.manager ? { manager_id: u.manager.user_id, name: u.manager.user_name } : undefined,
                    name: u.name,
                    organization_units: u.organization_units?.map(
                        ou => ({ name: ou.name, organization_unit_id: ou.organization_unit_id } as OrganizationUnit)
                    ),
                    out_of_office: u.out_of_office ? { from: u.out_of_office.from, to: u.out_of_office.to } : undefined,
                    phone_number: u.phone_number,
                    position: u.position,
                    user_groups: u.user_groups as UserGroup[],
                    user_id: u.user_id,
                    user_type: u.user_type as UserType
                }))
            };
        } else return null;
    },

    /**
     * Gets the current user
     * @param suppressErrors Whether to supress the throwing of exceptions on error
     * @returns the current user
     */
    async getCurrentUserAsync(suppressErrors: boolean = false): Promise<OwnUserGetDto | null> {
        const userResponse = await ModernApiController.getAsync<OwnUserGetDto>("users/me", suppressErrors);

        if (!IsProblemResult(userResponse)) return userResponse;
        else return null;
    }
};

/**
 * Checks whether the given filter requires that a 'post' filter be used (ao because of url length limitations)
 * @param filter The filter to check
 * @returns true if the filter requires a POST request to be done first, otherwise false
 */
function filterRequiresPost(filter: UserFilterPostDto): boolean {
    // check if any arrays are empty. If so, they should not go over the url
    if (filter) {
        const keys: (keyof UserFilterPostDto)[] = Object.keys(filter) as (keyof UserFilterPostDto)[];
        for (let k: number = 0; k < keys.length; k++) {
            const prop = filter[keys[k]];
            if (Array.isArray(prop) && prop.length == 0) return true;
        }
    }

    // require a post for every filter with user ids
    if (filter.user_ids) return true;
    if (filter.users_in) return true;

    // require a post if login code and name are both provided
    let numberOfTextFilters: number = 0;
    const textFilters: (keyof UserFilterPostDto)[] = ["login_code", "name", "name_login_code_email_contains", "user_group_id"];

    for (let k: number = 0; k < textFilters.length; k++) {
        if (filter[textFilters[k]]) numberOfTextFilters++;
        if (numberOfTextFilters > 1) return true;
    }

    // if one of the text filters is longer than 50 characters (if there were 2 or more filters, this point is never reached), require a post
    if (((filter.login_code || "") + (filter.name || "") + (filter.name_login_code_email_contains || "")).length > 50) return true;

    // all other cases do not require a post filter
    return false;
}
